import { PartsManagementFilter, PartsManagementFilterFormValue } from '../models/parts-management-filter.model';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, of, map, retry } from 'rxjs';
import { DownloadRequest } from '../models/download-filter.model';
import { utils } from '../helper/utils';
import { PartsManagementHeader } from '../models/parts-management-header.model';
import { AuditLog } from '../models/audit-log.model';
import { PartsManagementResponse } from '../models/parts-management-response.model';
import { CodeDescription } from '../models/code-description.model';
import { CreatePartRequest } from '../models/create-part-request.model';
import { PartBulkUploadResponse } from '../models/part-bulk-upload.model';

@Injectable({
  providedIn: 'root',
})
export class PartsManagementService {
  filterData: PartsManagementFilter = {};

  private readonly partsRefresh = new Subject<void>();
  /** Emits when the part list has to be re-fetched, e.g. after a bulk upload wrote rows. */
  readonly partsRefresh$ = this.partsRefresh.asObservable();

  constructor(private httpClient: HttpClient) {}

  triggerPartsRefresh(): void {
    this.partsRefresh.next();
  }

  getPartsManagementById(id: string): Observable<PartsManagementResponse> {
    return this.httpClient.get<PartsManagementResponse>(utils.GetMasterDataManagementUrl(`parts/${id}`));
  }

  getUniquePartManufacturerCodes(): Observable<CodeDescription[]> {
    return this.httpClient
      .get<{ key: string; value: string }[]>(utils.GetMasterDataManagementUrl('part-manufacturer/all'))
      .pipe(map((response) => response.map((item) => ({ code: item.key, description: item.value }))));
  }

  getUniquePartGroupCodes(): Observable<CodeDescription[]> {
    return this.httpClient
      .get<{ key: string; value: string }[]>(utils.GetMasterDataManagementUrl('part-groups/all'))
      .pipe(map((response) => response.map((item) => ({ code: item.key, description: item.value }))));
  }

  getPartsManagement(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: PartsManagementFilter,
  ): Observable<HttpResponse<PartsManagementHeader[]>> {
    const params: { [param: string]: string | number | boolean | string[] } = {};
    params['page'] = page;
    params['limit'] = pageLimit > 0 ? pageLimit : 100;
    if (sort) {
      params['sort'] = sort;
    }
    if (filters) {
      if (filters.partCodesList && Array.isArray(filters.partCodesList) && filters.partCodesList.length > 0) {
        params['partCodesList'] = filters.partCodesList.join(',');
      }
      if (
        filters.partManufacturerCodeList &&
        Array.isArray(filters.partManufacturerCodeList) &&
        filters.partManufacturerCodeList.length > 0
      ) {
        params['partManufacturerCodeList'] = filters.partManufacturerCodeList.join(',');
      }
      if (filters.partGroupCodeList && Array.isArray(filters.partGroupCodeList) && filters.partGroupCodeList.length > 0) {
        params['partGroupCodeList'] = filters.partGroupCodeList.join(',');
      }
      if (filters.description && Array.isArray(filters.description) && filters.description.length > 0) {
        params['description'] = filters.description.join(',');
      }
      if (filters.partActiveStatusesList && Array.isArray(filters.partActiveStatusesList) && filters.partActiveStatusesList.length > 0) {
        params['partActiveStatusesList'] = filters.partActiveStatusesList.join(',');
      }
      if (
        filters.isSerialTagNumberRequired &&
        Array.isArray(filters.isSerialTagNumberRequired) &&
        filters.isSerialTagNumberRequired.length > 0
      ) {
        params['isSerialTagNumberRequiredList'] = filters.isSerialTagNumberRequired.join(',');
      }
      if (filters.deductCore && Array.isArray(filters.deductCore) && filters.deductCore.length > 0) {
        params['deductCoreList'] = filters.deductCore.join(',');
      }
      if (filters && filters.searchText) {
        params['searchText'] = filters.searchText;
      }
    }

    const url = utils.GetMasterDataManagementUrl('parts');
    return this.httpClient.get<PartsManagementHeader[]>(url, { params, observe: 'response' }).pipe(
      map((response: HttpResponse<PartsManagementHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
          }));
          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
      catchError(() => {
        return of(new HttpResponse<PartsManagementHeader[]>({ body: [], headers: undefined }));
      }),
    );
  }

  extractFilterOptions(data: PartsManagementHeader[]): {
    partCodesList: string[];
    partManufacturerCodeList: string[];
    partGroupCodeList: string[];
    description: string[];
    partActiveStatusesList: string[];
    isSerialTagNumberRequired: string[];
    deductCore: string[];
  } {
    return {
      partCodesList: Array.from(new Set(data.map((item) => item.partCode).filter(Boolean))),
      partManufacturerCodeList: Array.from(new Set(data.map((item) => item.partManufacturerCode).filter(Boolean))),
      partGroupCodeList: Array.from(new Set(data.map((item) => item.partGroupCode).filter(Boolean))),
      description: Array.from(new Set(data.map((item) => item.description).filter(Boolean))),
      partActiveStatusesList: Array.from(new Set(data.map((item) => item.isActive).filter(Boolean))),
      isSerialTagNumberRequired: Array.from(new Set(data.map((item) => item.isSerialTagNumberRequired).filter(Boolean))),
      deductCore: Array.from(new Set(data.map((item) => item.deductCore).filter(Boolean))),
    };
  }

  private partsManagementFilterFormValue: PartsManagementFilterFormValue | null = null;
  setPartsManagementFilterFormValue(value: PartsManagementFilterFormValue | null) {
    this.partsManagementFilterFormValue = value;
  }
  getPartsManagementFilterFormValue(): PartsManagementFilterFormValue | null {
    return this.partsManagementFilterFormValue;
  }

  /**
   * Gets the part audit logs by part code.
   * @param partCode The part code (required)
   * @param isSystemLog Indicates if the request is for system logs (optional, default false)
   * @param page Page number (optional, default 1)
   * @param limit Items per page (optional, default 100)
   */
  getPartAuditLogs(
    partCode: string,
    isSystemLog: boolean = false,
    page: number = 1,
    limit: number = 100,
  ): Observable<HttpResponse<AuditLog[]>> {
    return this.httpClient
      .get<
        AuditLog[]
      >(utils.GetMasterDataManagementUrl(`parts/${encodeURIComponent(partCode)}/audit-logs?isSystemLog=${isSystemLog}&page=${page}&limit=${limit}`), { observe: 'response' })
      .pipe(
        retry(2),
        catchError((err) => {
          console.error('Error fetching part audit logs:', err?.message || err, 'Status:', err?.status);
          return of(new HttpResponse<AuditLog[]>({ body: [], status: 200, statusText: 'OK' }));
        }),
      );
  }
  createPart(partRequest: CreatePartRequest): Observable<PartsManagementResponse> {
    const url = utils.GetMasterDataManagementUrl('parts');
    return this.httpClient.post<PartsManagementResponse>(url, partRequest);
  }
  updatePart(partCode: string, partRequest: CreatePartRequest): Observable<PartsManagementResponse> {
    const url = utils.GetMasterDataManagementUrl(`parts/${partCode}`);
    return this.httpClient.put<PartsManagementResponse>(url, partRequest);
  }

  downloadToExcel(request: DownloadRequest): Observable<Blob> {
    const url = utils.GetDownloadUrl('part');
    return this.httpClient.post(url, request, { responseType: 'blob' });
  }

  /**
   * Upserts part master data from a templated .xlsx workbook.
   * The call is synchronous on the API side, so a full 90,000 row file takes a while to come back.
   */
  bulkUploadParts(file: File): Observable<PartBulkUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.httpClient.post<PartBulkUploadResponse>(utils.GetMasterDataManagementUrl('parts/bulk-upload'), formData);
  }
}
